/**
 * MailverlaufTab — chronologischer Mail-Thread pro Lead (Plan 5.1-04 / D-05 + D-06).
 *
 *  - Liste oben → unten = aelteste → neueste Mail
 *  - Inbound vs. Outbound visuell unterscheidbar (Farbe + Pfeil-Icon)
 *  - Volle HTML-Bodies werden mit DOMPurify sanitized BEVOR sie ins DOM landen
 *    (XSS-Schutz — body_html kommt aus untrusted Source = eingehende Mails)
 *  - Antworten-Button oeffnet MailComposeModal
 */

import { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { ArrowDown, ArrowUp, Mail, RefreshCw, Reply, Loader2 } from 'lucide-react';
import { Button } from '@libero/core/components/ui/button';
import { leadsApi } from '../../services/leadsApi';
import type { Lead, LeadMail } from '../../types';
import MailComposeModal from '../MailComposeModal';

interface Props {
  lead: Lead;
}

export default function MailverlaufTab({ lead }: Props) {
  const [mails, setMails] = useState<LeadMail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const list = await leadsApi.mails(lead.id);
      setMails(list);
    } catch (e: any) {
      setError(e.response?.data?.error?.message ?? 'Mailverlauf konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.id]);

  const lastInbound = [...mails].reverse().find((m) => m.direction === 'inbound');
  const lastSubject = lastInbound?.subject ?? '';

  if (loading) {
    return (
      <div className="py-8 text-sm text-muted-foreground flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Lade Mailverlauf...
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2 flex items-center gap-2">
          <span>{error}</span>
          <Button size="sm" variant="outline" onClick={() => void load()}>
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Erneut versuchen
          </Button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-[#1A1A1A]">
          Mailverlauf <span className="text-muted-foreground font-normal">({mails.length})</span>
        </h2>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => void load()}>
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Neu laden
          </Button>
          <Button
            size="sm"
            onClick={() => setComposeOpen(true)}
            className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
          >
            <Reply className="w-3.5 h-3.5 mr-1" /> Antworten
          </Button>
        </div>
      </div>

      {mails.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground border-2 border-dashed rounded">
          <Mail className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Noch keine Mails fuer diesen Lead
        </div>
      ) : (
        <div className="space-y-3">
          {mails.map((m) => (
            <MailCard key={m.id} mail={m} />
          ))}
        </div>
      )}

      {composeOpen && (
        <MailComposeModal
          lead={lead}
          replyToSubject={lastSubject}
          replyToMessageId={lastInbound?.messageId ?? null}
          onClose={() => setComposeOpen(false)}
          onSent={() => {
            setComposeOpen(false);
            void load();
          }}
        />
      )}
    </div>
  );
}

// ── MailCard ───────────────────────────────────────────────────────────────

function MailCard({ mail }: { mail: LeadMail }) {
  const isInbound = mail.direction === 'inbound';
  const accentBg = isInbound ? 'bg-[#DAD9D9]/40' : 'bg-[#E9FF57]/30';
  const accentBorder = isInbound ? 'border-[#DAD9D9]' : 'border-[#E9FF57]';

  // CI-Regel: nie weisse Schrift auf gelbem Hintergrund — wir nutzen #1A1A1A
  return (
    <div className={`border ${accentBorder} ${accentBg} rounded p-3`}>
      <div className="flex items-center gap-2 mb-2 text-xs text-[#1A1A1A]">
        {isInbound ? (
          <span className="flex items-center gap-1 font-semibold">
            <ArrowDown className="w-3.5 h-3.5" /> Eingang
          </span>
        ) : (
          <span className="flex items-center gap-1 font-semibold">
            <ArrowUp className="w-3.5 h-3.5" /> Gesendet
          </span>
        )}
        <span className="text-muted-foreground">·</span>
        <span>{formatDateTime(mail.sentAt)}</span>
        <span className="text-muted-foreground">·</span>
        <span className="truncate">
          {mail.fromAddress} <span className="text-muted-foreground">→</span> {mail.toAddress}
        </span>
      </div>
      <div className="font-semibold text-sm mb-2 text-[#1A1A1A]">{mail.subject || '(kein Betreff)'}</div>
      <MailBody mail={mail} />
    </div>
  );
}

// ── MailBody (mit DOMPurify Sanitization) ──────────────────────────────────

function MailBody({ mail }: { mail: LeadMail }) {
  if (mail.bodyHtml) {
    return <MailHtmlIframe html={mail.bodyHtml} />;
  }
  if (mail.bodyText) {
    return (
      <pre className="text-sm whitespace-pre-wrap max-h-96 overflow-auto bg-white/60 rounded p-2 font-sans">
        {mail.bodyText}
      </pre>
    );
  }
  return <div className="text-sm text-muted-foreground italic">(Kein Inhalt)</div>;
}

/**
 * HTML-Mail in iframe gerendert — Gmail/Outlook-Pattern.
 *
 * Warum iframe statt direkt ins DOM?
 *  - CSS-Isolation: Mail-eigene Styles (line-height:0.5, font-family etc.) brechen
 *    sonst das Tool-Layout (Ueberlappungen, falsche Schriften).
 *  - Sandboxing: `sandbox=""` deaktiviert JS-Execution + Top-Navigation komplett.
 *  - DOMPurify bleibt als 2. Layer drin (defense in depth).
 *
 * Dynamische Hoehe: nach Iframe-Load scrollHeight messen und Iframe-Hoehe setzen.
 */
function MailHtmlIframe({ html }: { html: string }) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [height, setHeight] = useState<number>(120);

  const sanitized = DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    ADD_ATTR: ['target'],
  });

  // srcDoc-Wrapper mit minimalem Base-CSS: reset margins + brauchbare Default-Schrift.
  // Wir respektieren Mail-eigene Styles (kein !important-Override), aber sorgen fuer
  // sinnvolle Basis-Werte falls die Mail keine setzt.
  const srcDoc = `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>
    html,body{margin:0;padding:8px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:14px;color:#1A1A1A;line-height:1.4;background:transparent;word-wrap:break-word}
    a{color:#1A1A1A;text-decoration:underline}
    img{max-width:100%;height:auto}
    table{max-width:100%}
  </style></head><body>${sanitized}</body></html>`;

  function handleLoad() {
    const iframe = iframeRef.current;
    if (!iframe?.contentDocument) return;
    try {
      const docHeight = Math.min(
        iframe.contentDocument.documentElement.scrollHeight + 16,
        600, // Max-Hoehe — laengere Mails scrollen intern
      );
      setHeight(Math.max(docHeight, 80));
    } catch {
      // contentDocument-Zugriff blockiert? Fallback: feste Hoehe.
      setHeight(300);
    }
  }

  return (
    <iframe
      ref={iframeRef}
      title="Mail-Inhalt"
      srcDoc={srcDoc}
      sandbox=""
      onLoad={handleLoad}
      className="w-full bg-white/60 rounded border-0 overflow-auto"
      style={{ height: `${height}px` }}
    />
  );
}

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
